/**
 * VerificationBadge - chip-style badge with verified.png icon + label text.
 *
 * Usage:
 *   <VerificationBadge />               - default (md) - chip with label
 *   <VerificationBadge size="sm" />     - smaller chip, shorter label
 *   <VerificationBadge size="lg" />     - larger chip for profile headers
 *   <VerificationBadge showLabel={false} /> - icon-only (no text)
 */

import React from 'react';

interface VerificationBadgeProps {
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  tooltip?: string;
  className?: string;
}

const SIZE_CONFIG: Record<
  NonNullable<VerificationBadgeProps['size']>,
  { px: number; chipClass: string; textClass: string; label: string }
> = {
  sm: {
    px: 14,
    chipClass: 'px-1.5 py-0.5 gap-1 rounded-full text-[10px]',
    textClass: 'font-semibold',
    label: 'Verificado',
  },
  md: {
    px: 16,
    chipClass: 'px-2 py-0.5 gap-1.5 rounded-full text-xs',
    textClass: 'font-semibold',
    label: 'Verificado',
  },
  lg: {
    px: 20,
    chipClass: 'px-3 py-1 gap-2 rounded-full text-sm',
    textClass: 'font-bold',
    label: 'Profissional Verificado',
  },
};

export default function VerificationBadge({
  size = 'md',
  showLabel = true,
  tooltip = 'Profissional Verificado',
  className = '',
}: VerificationBadgeProps) {
  const { px, chipClass, textClass, label } = SIZE_CONFIG[size];

  return (
    <span
      title={tooltip}
      className={`inline-flex items-center bg-emerald-50 text-emerald-700 border border-emerald-200 ${chipClass} ${className}`}
      style={{ flexShrink: 0 }}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/verified.png"
        alt=""
        width={px}
        height={px}
        style={{ display: 'block', flexShrink: 0 }}
      />
      {showLabel && <span className={textClass}>{label}</span>}
    </span>
  );
}
